import React from 'react';

import { Button, Group, Modal, ModalProps, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import useDeleteGoogleCalendarIntegration from 'hooks/googleCalendarIntegration/useDeleteGoogleCalendarIntegration';
import { GoogleCalendarIntegration } from 'types';
import notificationHandler from 'utils/notifications';

interface DeleteIntegrationModalProps extends Pick<ModalProps, 'opened' | 'onClose'> {
  googleCalendarIntegration: GoogleCalendarIntegration;
}

const DeleteIntegrationModal = ({
  opened,
  onClose,
  googleCalendarIntegration,
}: DeleteIntegrationModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const deleteGoogleCalendarIntegrationMutation = useDeleteGoogleCalendarIntegration();

  const handleGoogleCalendarIntegrationDelete = (gci: GoogleCalendarIntegration): void => {
    deleteGoogleCalendarIntegrationMutation.mutate(gci.googleCalendarIntegrationId, {
      onError: error => {
        errorNotification('Google Calendar Integration delete failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification(
          'Google Calendar Integration deleted. Events are being removed from your calendar in the background.',
        );
      },
    });
  };

  const handleSubmit = (): void => {
    handleGoogleCalendarIntegrationDelete(googleCalendarIntegration);
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size='lg'
      title={<Title order={3}>Delete Integration?</Title>}
    >
      <Stack>
        <Stack p='md' align='center'>
          <KiloAlert variantStyle='warning'>
            Only delete this integration if you want to change your Google account or if you want to{' '}
            {<b>permanently</b>} delete this integration.
          </KiloAlert>
          <KiloAlert>
            To resolve issues with your synced calendar please use the <strong>Force Resync</strong> button.
          </KiloAlert>
          <Text size='lg' fw={500}>
            Are you sure you want to delete this integration?{' '}
          </Text>
        </Stack>
        <Group position='right'>
          <Button
            disabled={deleteGoogleCalendarIntegrationMutation.isLoading}
            onClick={onClose}
            variant='outline'
          >
            Cancel
          </Button>
          <Button
            color='red'
            loading={deleteGoogleCalendarIntegrationMutation.isLoading}
            onClick={handleSubmit}
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
export default DeleteIntegrationModal;
